import { ArrowDown, ChevronRight, Home } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const routeLabels = {
  about: "About us",
  blog: "Blog",
  contact: "Contact us",
  cookies: "Cookie policy",
  faqs: "FAQs",
  "how-it-works": "How it works",
  legal: "Legal",
  pricing: "Pricing",
  privacy: "Privacy policy",
  services: "Services",
  terms: "Terms of service",
  testimonials: "Testimonials",
  "acceptable-use": "Acceptable use",
};

function makeLabel(segment) {
  return routeLabels[segment] || segment.replace(/-/g, " ");
}

export default function PageHero({
  eyebrow = "The Bridge by VA",
  title,
  copy,
  children,
}) {
  const { pathname } = useLocation();
  const segments = pathname.split("/").filter(Boolean);

  function handleExplore(event) {
    const content = document.getElementById("content");
    if (!content) return;
    event.preventDefault();
    content.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <section className="relative overflow-hidden bg-ink pb-20 pt-36 text-white lg:pb-28 lg:pt-44">
      <div className="tech-grid absolute inset-0 opacity-60" />
      <div className="absolute -right-32 top-8 h-80 w-80 rounded-full bg-ocean/20 blur-[100px]" />
      <div className="absolute bottom-0 left-1/4 h-48 w-48 rounded-full bg-bridge/10 blur-[90px]" />
      <div className="container relative">
        <nav aria-label="Breadcrumb" className="mb-7 flex flex-wrap items-center gap-1.5 text-xs font-bold text-slate-300">
          <Link to="/" className="inline-flex items-center gap-1.5 transition hover:text-cyan">
            <Home size={13} />
            Home
          </Link>
          {segments.map((segment, index) => {
            const isCurrent = index === segments.length - 1;
            const destination = `/${segments.slice(0, index + 1).join("/")}`;
            const label = isCurrent ? title : makeLabel(segment);

            return (
              <span key={destination} className="flex min-w-0 items-center gap-1.5">
                <ChevronRight size={13} className="shrink-0 text-slate-500" />
                {isCurrent ? (
                  <span aria-current="page" className="max-w-[16rem] truncate text-white sm:max-w-[28rem]">
                    {label}
                  </span>
                ) : (
                  <Link to={destination} className="transition hover:text-cyan">
                    {label}
                  </Link>
                )}
              </span>
            );
          })}
        </nav>
        <p className="eyebrow text-cyan">{eyebrow}</p>
        <h1 className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-[-.055em] sm:text-6xl lg:text-7xl">
          {title}
        </h1>
        {copy && (
          <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
            {copy}
          </p>
        )}
        {children}
        <a
          href="#content"
          onClick={handleExplore}
          className="mt-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[.2em] text-cyan hover:text-white"
        >
          Explore <ArrowDown size={14} />
        </a>
      </div>
    </section>
  );
}
